// src/app/components/fix-actions/artifact-claim-check.tsx
// #132: an asset's claim check against the brand record, where its
// confidence figure used to show (that figure was the model grading itself).
// Every verdict comes from the server (app/services/fix_actions_service.py);
// this file lays it out.

import type { Claim, ClaimSummary } from "@/api/content";

export type ArtifactClaimCheck = {
  evidence?: string;
  brief_id?: number | null;
  customer_id?: number | null;
  claims?: Claim[];
  summary?: ClaimSummary | null;
  note?: string;
  failed?: boolean;
} | null | undefined;

type Tone = "ok" | "warn" | "muted";

const TONES: Record<Tone, { bg: string; fg: string }> = {
  ok: { bg: "var(--success-bg)", fg: "var(--success-text)" },
  warn: { bg: "var(--warning-bg)", fg: "var(--warning-text)" },
  muted: { bg: "var(--muted)", fg: "var(--muted-foreground)" },
};

function isBlocking(c: Claim) {
  if (c.resolution) return false;
  return c.status !== "supported" || !c.model_checked;
}

export function claimCheckState(check: ArtifactClaimCheck): { text: string; tone: Tone; title: string } {
  if (!check) {
    return { text: "Not checked", tone: "muted", title: "Generated before assets were checked against the brand record" };
  }
  if (check.failed) {
    return { text: "Claim check did not run", tone: "warn", title: check.note || "" };
  }
  if (check.evidence === "none") {
    return { text: "No brand record", tone: "muted", title: check.note || "" };
  }
  const blocking = (check.claims || []).filter(isBlocking).length;
  if (blocking > 0) {
    return { text: `${blocking} claim${blocking === 1 ? "" : "s"} to resolve`, tone: "warn",
             title: "Unsupported or not yet checked: this asset cannot be marked ready until they are resolved" };
  }
  const total = (check.claims || []).length;
  return { text: total ? "Claims supported" : "No factual claims", tone: "ok",
           title: total ? `Every claim (${total}) is supported by the brand record` : "" };
}

export function ClaimCheckBadge({ check }: { check: ArtifactClaimCheck }) {
  const s = claimCheckState(check);
  const t = TONES[s.tone];
  return (
    <span title={s.title} style={{ display: "inline-flex", alignItems: "center", fontSize: 11, fontWeight: 600,
                                   padding: "2px 9px", borderRadius: 9999, background: t.bg, color: t.fg,
                                   border: "1px solid var(--border)", whiteSpace: "nowrap" }}>
      {s.text}
    </span>
  );
}

/** The badge, the note, and each claim that still blocks with why. */
export function ClaimCheckDetails({ check }: { check: ArtifactClaimCheck }) {
  const blocking = (check?.claims || []).filter(isBlocking);
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 10, padding: "12px 14px", margin: "0 0 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: check?.note || blocking.length ? 6 : 0 }}>
        <strong style={{ fontSize: 12 }}>Claim check</strong>
        <ClaimCheckBadge check={check} />
      </div>
      {check?.note && <p style={{ fontSize: 12, color: "var(--muted-foreground)", margin: "0 0 6px" }}>{check.note}</p>}
      {blocking.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.5 }}>
          {blocking.map(c => (
            <li key={c.sentence_index}>
              {c.sentence}
              <span style={{ color: "var(--muted-foreground)" }}>
                {" "}· {c.status === "supported" ? "not yet checked by the model" : c.status}
                {c.note ? `: ${c.note}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
      {blocking.length > 0 && (
        <p style={{ fontSize: 12, color: "var(--muted-foreground)", margin: "6px 0 0" }}>
          Edit or regenerate the asset, or send it to Brand Publisher and resolve the claims there.
        </p>
      )}
    </div>
  );
}
